import { Fragment, useCallback, useEffect, useMemo } from 'react'
import { useSessionStorage } from 'react-use'
import clsx from 'clsx'
import dayjs from 'dayjs'

import { ColumnConditionValue, IActionRepeaterValue, TypographyGroupValue } from '@makyoapp/makyo-common'
import {
  BaseComponent,
  Calendar,
  cn,
  ColorVariant,
  CornerRadius,
  EventProps,
  isColorDark,
  parseSpacing,
  RawSpacingInput,
  Skeleton,
  SkeletonGroup,
  spacingToStyles,
  TypographyValue,
  useCalendarState,
  useMtwContext,
} from '@makyoapp/makyo-tw-ui'

import { CoreComponentAction, CoreComponentInfos, CoreComponentTableData } from '@/core/models/app-components/types'
import { ICell } from '@/core/models/columns'
import { DateTimeCell } from '@/core/models/columns/dateTime'
import { FunctionCell } from '@/core/models/columns/function'
import { LookupCell } from '@/core/models/columns/lookup'
import { useAppBuilderContext } from '@/context/AppBuilderContext'
import { useCompTableData } from '@/hooks/__project__/useCompTableData'
import { useBreakpoint } from '@/hooks/useBreakpoint'
import { ComponentSettingKey, SpacingInputValue, UserColumnFilterValue } from '@/shared/builder'
import { MAX_ROWS_LIMIT } from '@/shared/constants'
import { getSafely } from '@/shared/safely'
import { CompOverlayDevmode } from '@/components/__project__/shared/CompOverlayDevmode'

import { useCompPagination } from '../../hooks/useCompPagination'
import { CompFilterV2 } from '../../shared/CompFilterV2'

type ScreenSize = 'tablet' | 'desktop' | 'mobile' | 'large' | undefined

function parseDateCell(cell: ICell, defaultDate: Date) {
  if (!cell) return defaultDate

  if (cell.value instanceof FunctionCell) {
    return (cell.value as FunctionCell).toReturnTypeCell().value as Date
  }

  if (cell.value instanceof LookupCell) {
    return (cell.value as LookupCell).toReturnTypeCell()[0].value as Date
  }

  if (cell instanceof DateTimeCell) {
    return cell.value as Date
  }

  if (dayjs(String(cell.value)).isValid()) {
    return new Date(String(cell.value))
  }

  return defaultDate
}

export interface CompCalendarWrapperCoreProps {
  infos: CoreComponentInfos
  action: CoreComponentAction
  tableData: CoreComponentTableData
}

export function CompCalendarWrapperCore({
  infos: { settings, component, currentParentBg },
  action: { simulateAction },
  tableData: { dbTable: table },
}: CompCalendarWrapperCoreProps) {
  const { device } = useBreakpoint()
  const { isDevelopmentMode, openComponentSettings } = useAppBuilderContext()

  const { globalDoc } = useMtwContext()

  // ========================= data =========================
  const compId = component._id

  // calendar state
  const [calendarState, setCalendarState] = useCalendarState(`calendar-${compId}`, {
    currentDate: new Date().toISOString(),
    selectedDate: new Date().toISOString(),
    view: settings[ComponentSettingKey?.CalendarBehaviorDefaultView] ?? 'month',
  })

  useEffect(() => {
    const newView = settings[ComponentSettingKey?.CalendarBehaviorDefaultView] ?? 'month'
    setCalendarState((prev) => ({
      ...prev,
      view: newView,
    }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings[ComponentSettingKey?.CalendarBehaviorDefaultView], setCalendarState])

  // columns
  const colTitle = settings[ComponentSettingKey.CalendarDataTitle] as string
  const colStartDate = settings[ComponentSettingKey.CalendarDataStartDate] as string
  const colEndDate = settings[ComponentSettingKey.CalendarDataEndDate] as string
  const colColor = settings[ComponentSettingKey.CalendarDataColor] as string

  // dataset limit
  const datasetLimit = settings[ComponentSettingKey.CalendarDataSetLimit]
  const isLimited = datasetLimit?.enabled
  const limit = datasetLimit?.value ?? 0
  const maxPerItems = isLimited ? limit : MAX_ROWS_LIMIT

  const userFilterSetting: Array<UserColumnFilterValue> = useMemo(() => {
    const stg = settings[ComponentSettingKey.CalendarBehaviorUserFilter] as UserColumnFilterValue[]
    return Array.isArray(stg) ? stg : []
  }, [settings])

  const [userFilter, setUserFilter] = useSessionStorage(`userFilter-${compId}`, {})

  // filter
  const filterOverlayBgColor = settings[ComponentSettingKey.CalendarDesignFilterBgColor] ?? 'primary-95'
  const filterChipColor = settings[ComponentSettingKey.CalendarDesignFilterChipColor] ?? 'primary-95'
  const filterCornerRadius = settings[ComponentSettingKey.CalendarDesignFilterCornerRadius] as CornerRadius

  // pagination
  const { pagination } = useCompPagination({
    compId,
    defaultPerPage: maxPerItems,
  })

  const filterSetting = settings[ComponentSettingKey.CalendarDataFilter] as ColumnConditionValue | undefined

  const sortBy = settings[ComponentSettingKey.CalendarDataSortBy] as string
  const sortType = settings[ComponentSettingKey.CalendarDataSortType] as string

  // fetch all rows
  const [{ rows, allRowsCount }, isLoading] = useCompTableData({
    table,
    componentId: component._id,
    filterSetting,
    sortSetting: { sortType, sortBy },
    pagination,
    userFilter,
  })

  const backgroundColor = settings[ComponentSettingKey?.CalendarDesignBackgroundColor] ?? 'primary-0'

  const hexBackground =
    getComputedStyle(globalDoc.documentElement).getPropertyValue(`--color-${backgroundColor}`) || '#ffffff'

  const getColorForId = useCallback(
    (id: number) => {
      const tones = isColorDark(hexBackground) ? [60, 70, 80, 90, 95] : [5, 10, 20, 30, 40]
      const prefix = ['primary', 'secondary', 'tertiary']
      const tone = tones[id % tones.length]
      const prefixs = prefix[id % prefix.length]
      return `${prefixs}-${tone}`
    },
    [hexBackground]
  )

  const events: EventProps[] = useMemo(() => {
    // make sure start & end date specified
    if (!colStartDate) return []

    return rows.map((row, i) => {
      const startDate = parseDateCell(row[colStartDate], new Date())
      const endDate = colEndDate ? parseDateCell(row[colEndDate], startDate) : startDate

      const eventId = Number(i + 1)
      return {
        _id: row._id,
        id: eventId,
        title: row[colTitle]?.toString() ?? '',
        start: startDate,
        end: endDate,
        color: colColor ? row[colColor]?.toString() : getColorForId(eventId),
      }
    })
  }, [colStartDate, colEndDate, rows, colTitle, colColor, getColorForId])

  // ========================= styles =========================
  // base
  const width = getSafely(settings, [ComponentSettingKey.CalendarDesignWidth], ['100%'])[0]
  const alignment = settings[ComponentSettingKey?.CalendarDesignCompHAlign] ?? 'parent'
  const margin = settings[ComponentSettingKey?.CalendarDesignMargin] as RawSpacingInput
  const padding = settings[ComponentSettingKey?.CalendarDesignPadding] as RawSpacingInput

  const typoSettings = settings[ComponentSettingKey?.CalendarDesignTypography] as TypographyGroupValue
  const titleTypography = typoSettings[ComponentSettingKey?.CalendarDesignTitleTypography] as TypographyValue
  const contentTypography = typoSettings[ComponentSettingKey?.CalendarDesignContentTypography] as TypographyValue
  const dateTypography = typoSettings[ComponentSettingKey?.CalendarDesignDateTypography] as TypographyValue
  const cornerRadius = settings[ComponentSettingKey?.CalendarDesignCornerRadius] as SpacingInputValue
  const showShadow = settings[ComponentSettingKey?.CalendarDesignElevation]

  // ========================= actions =========================
  const onClickEventActions = getSafely(
    settings,
    [ComponentSettingKey.CalendarActionOnClick],
    []
  ) as IActionRepeaterValue[]
  const onAddEventActions = getSafely(
    settings,
    [ComponentSettingKey.CalendarActionAddEvent],
    []
  ) as IActionRepeaterValue[]

  const screenSize: ScreenSize =
    device === 'phone' || device === 'tablet' ? 'mobile' : device === 'tablet-landscape' ? 'tablet' : 'desktop'

  const onClickEvent = useCallback(
    (e: EventProps) => {
      if (!!onClickEventActions.length) {
        simulateAction({ actions: onClickEventActions, rowId: e._id, tableId: table.settings._id })
      }
    },
    [onClickEventActions, simulateAction, table.settings._id]
  )

  const onAddEvent = useCallback(() => {
    if (!!onAddEventActions.length) {
      simulateAction({ actions: onAddEventActions, rowId: '', tableId: table.settings._id })
    }
  }, [onAddEventActions, simulateAction, table.settings._id])

  // ========================= behaviors =========================
  const autoFitDay = settings[ComponentSettingKey?.CalendarBehaviorAutoFitDay]

  // ========================= render =========================
  const handleClick = () => {
    openComponentSettings(component)
    if (isDevelopmentMode) return
  }

  const calendarComponent = (
    <BaseComponent
      id={component._id}
      width={width}
      alignment={alignment}
      margin={margin}
      backgroundColor={currentParentBg}
      className={clsx('box-border')}
    >
      <CompFilterV2
        id={compId}
        value={userFilter}
        setValue={setUserFilter}
        settings={userFilterSetting}
        table={table}
        filterChipColor={filterChipColor}
        filterOverlayBgColor={filterOverlayBgColor}
        filterCornerRadius={filterCornerRadius}
        filterMargin={settings[ComponentSettingKey.CalendarDesignFilterMargin]}
      />
      <Calendar
        // base
        id={compId}
        width={width}
        alignment={alignment}
        padding={padding}
        margin={margin}
        backgroundColor={backgroundColor}
        // calendar
        state={calendarState}
        onStateChange={setCalendarState}
        screenSize={screenSize}
        events={events}
        titleTypography={titleTypography}
        eventTypography={contentTypography}
        onClickEvent={onClickEvent}
        onAddEvent={onAddEvent}
        cornerRadius={cornerRadius as any}
        autoFitDay={autoFitDay}
        showShadow={showShadow}
        showAddEventButton={false}
        startHour={0}
        endHour={24}
      />
    </BaseComponent>
  )

  return isLoading ? (
    <CalendarSkeleton backgroundColor={currentParentBg!} margin={margin} />
  ) : isDevelopmentMode ? (
    <div className='relative flex flex-col w-full border border-dashed' style={{ borderColor: '#333' }}>
      {calendarComponent}
      <CompOverlayDevmode handleClick={handleClick} />
    </div>
  ) : (
    <Fragment>{calendarComponent}</Fragment>
  )
}

const CalendarSkeleton = ({ backgroundColor, margin }: { backgroundColor: ColorVariant; margin: RawSpacingInput }) => {
  return (
    <SkeletonGroup backgroundColor={backgroundColor}>
      <div className='w-full flex-1' style={{ ...spacingToStyles(parseSpacing(margin), 'padding') }}>
        <div className='py-4 flex items-center justify-between'>
          <Skeleton className='w-52 h-9 rounded-lg' />
          <Skeleton className='w-24 h-9 rounded-lg' />
        </div>

        <Skeleton className='flex-1 w-full h-[638px] rounded-lg shrink-0' />
      </div>
    </SkeletonGroup>
  )
}
